import React from 'react'
import Head from 'components/head'

import { getImage, getSocialShareImage } from 'util/image'
import { toISO } from 'util/datetime'
import { isCorpOrFaction } from 'util/helpers'

import ArticleType, { ArticleExtType } from 'store/types/ts/article'
import CategoryType from 'store/types/ts/category'
import AuthorType from 'store/types/ts/author'
import TopicType from 'store/types/ts/topic'

interface Props {
  article: ArticleType | ArticleExtType
  category: CategoryType
  author: AuthorType
  topics: Array<TopicType>
  defaultImage: string
  defaultDescription: string
}

export const ArticleHelmet = ({
  article,
  category,
  author,
  topics,
  defaultImage,
  defaultDescription,
}: Props) => {
  // Using same size images for sharing open graph and twitter, this is the recommended banner size

  // Generate special images for categories that have icon images

  let image: string
  const isSpecial = isCorpOrFaction(category.id)

  if (isSpecial) {
    // This is for render background color on corp and factions with logo on top
    image = getImage([article.image, category.image], defaultImage, {
      w: 1200,
      h: 630,
      fm: 'png',
      fit: 'pad',
      bg: 'rgb:101010',
    })
  } else {
    image = getSocialShareImage([article.image, category.image], defaultImage)
  }

  return (
    <Head>
      <title>{article.title}</title>
      <meta property="og:title" content={article.title} />
      <meta property="og:type" content="article" />
      <meta name="description" content={article.description || defaultDescription} />
      <meta property="og:description" content={article.description || defaultDescription} />
      {article.publicationDate && (
        <meta property="article:published_time" content={toISO(article.publicationDate)} />
      )}
      {article.updatedAt && (
        <meta property="article:modified_time " content={toISO(article.updatedAt)} />
      )}
      <meta property="article:section" content={category.name} />
      <meta name="twitter:label1" content="Category" />
      <meta name="twitter:data1" content={category.name} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content={image} />
      <meta property="og:image" content={image} />
      {author && <meta key={author.id} property="article:author" content={author.name} />}
      {author && <meta name="twitter:label2" content="Author" />}
      {author && <meta name="twitter:data2" content={author.name} />}
      {topics.map((topic) => (
        <meta key={topic.id} property="article:tag" content={topic.name} />
      ))}
    </Head>
  )
}
